import { ReactNode } from 'react';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import { TruncatedText } from '@/components/atoms/TruncatedText/TruncatedText';
import { cn } from '@/lib/utils';

type Props = { label: string; children: ReactNode; copyValue?: string };

/**
 * Two-column `label · value` line of the replay info panels, with an optional
 * copy button. The value column keeps a fixed 260px basis so every value starts
 * at the same x; the label clips rather than wraps, which would break that.
 */
export const InfoRow = ({ label, children, copyValue }: Props) => (
  <div className="flex items-start gap-4 self-stretch pl-0.5">
    <span
      title={label}
      className="min-w-0 flex-1 truncate text-sm leading-5 font-normal text-muted-foreground"
    >
      {label}
    </span>
    {/* Allowed to shrink — a hard `w-65 shrink-0` overflows a narrow panel. */}
    <div className="flex w-65 min-w-0 basis-65 items-center gap-2 self-stretch">
      <div className="flex min-w-0 flex-1 text-sm leading-5 font-normal text-foreground">
        {children}
      </div>
      {copyValue && (
        <CopyButton
          data={copyValue}
          buttonSize="flush"
          className="flex size-4 shrink-0 items-center hover:[&_svg]:stroke-foreground hover:[&_svg]:text-foreground"
        />
      )}
    </div>
  </div>
);

/** `InfoRow` for a plain text value: clips with a tooltip, copyable, hidden when empty. */
export const InfoTextRow = ({ label, value }: { label: string; value?: string | null }) => {
  if (!value) {
    return null;
  }
  return (
    <InfoRow label={label} copyValue={value}>
      <TruncatedText text={value} className="min-w-0 flex-1" />
    </InfoRow>
  );
};

/** Heading of a panel section ("User info", "Conversation info", …). */
export const PanelSectionTitle = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <h3 className={cn('text-sm leading-5 font-medium text-foreground', className)}>{children}</h3>
);
